import React, { useEffect, useState, useCallback } from 'react';
import Component from '../../components/Component';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
    getCurrentMorningBrief,
    regenerateMorningBrief,
} from '../../services/morningBriefAdminApi';

// IST calendar-day label (matches what the cron uses for the persisted day_key).
// Uses Intl with timeZone:'Asia/Kolkata' so it's correct regardless of the
// browser's own timezone.
function todayIstLabel() {
    try {
        return new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Kolkata',
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }).format(new Date());
    } catch {
        return new Date().toDateString();
    }
}

function cacheChip(source) {
    if (!source) return null;
    const map = {
        redis:    { label: 'redis',    color: '#1976d2' },
        postgres: { label: 'postgres', color: '#7b1fa2' },
        fresh:    { label: 'fresh',    color: '#2e7d32' },
    };
    const m = map[source] || { label: String(source), color: '#616161' };
    return <Chip label={m.label} size="small" sx={{ bgcolor: m.color, color: '#fff', fontWeight: 600 }} />;
}

function BriefRow({ brief, idx }) {
    return (
        <Card variant="outlined" sx={{ mb: 1 }}>
            <CardContent sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Typography variant="caption" sx={{ minWidth: 28, fontWeight: 700, color: 'text.secondary' }}>
                        #{idx + 1}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                            {brief.title || <em>(no title)</em>}
                        </Typography>
                        {brief.match_short_title && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {brief.match_short_title}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {brief.box_text}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

function MorningBriefAdmin() {
    const [loading, setLoading] = useState(false);
    const [regenLoading, setRegenLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [briefData, setBriefData] = useState(null);     // { briefs_count, cached_from, payload }
    const [lastRegen, setLastRegen] = useState(null);      // { ms, count }

    const loadCurrent = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const res = await getCurrentMorningBrief();
            setBriefData(res?.data || null);
        } catch (err) {
            if (/no morning brief/i.test(err.message || '')) {
                setBriefData(null);
                setInfo('No brief generated yet today — hit Regenerate to build one.');
            } else {
                setError(err.message || 'Failed to load current brief');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadCurrent(); }, [loadCurrent]);

    const handleRegen = async () => {
        setConfirmOpen(false);
        setRegenLoading(true); setError(''); setInfo('');
        const startedAt = Date.now();
        try {
            const res = await regenerateMorningBrief();
            const data = res?.data || {};
            setLastRegen({ ms: data.generated_in_ms || (Date.now() - startedAt), count: data.briefs_count || 0 });
            setBriefData({
                briefs_count: data.briefs_count,
                cached_from: 'fresh',
                payload: data.payload,
            });
            setInfo(`Regenerated ${data.briefs_count} briefs in ${((data.generated_in_ms || 0) / 1000).toFixed(1)}s`);
        } catch (err) {
            setError(err.message || 'Regeneration failed');
        } finally {
            setRegenLoading(false);
        }
    };

    const briefs = briefData?.payload?.briefs || [];

    return (
        <Component>
            <div className="page-body">
                <div className="container-fluid">
                    <Box sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Box>
                                <Typography variant="h5" fontWeight={700}>Daily Digest — Morning Brief</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    One global digest per day. Cron runs at 07:00 IST. Use Regenerate to force a fresh build mid-day.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={loadCurrent}
                                    disabled={loading || regenLoading}
                                >
                                    Refresh view
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={regenLoading ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                                    onClick={() => setConfirmOpen(true)}
                                    disabled={regenLoading || loading}
                                >
                                    {regenLoading ? 'Regenerating…' : 'Regenerate now'}
                                </Button>
                            </Stack>
                        </Stack>

                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                                            Day (IST)
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>{todayIstLabel()}</Typography>
                                    </Box>
                                    <Divider orientation="vertical" flexItem />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                                            Briefs
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>{briefData?.briefs_count ?? '—'}</Typography>
                                    </Box>
                                    <Divider orientation="vertical" flexItem />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                                            Source
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>{cacheChip(briefData?.cached_from)}</Box>
                                    </Box>
                                    {lastRegen && (
                                        <>
                                            <Divider orientation="vertical" flexItem />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                                                    Last regen
                                                </Typography>
                                                <Typography variant="subtitle1" fontWeight={700}>{(lastRegen.ms / 1000).toFixed(1)}s</Typography>
                                            </Box>
                                        </>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>

                        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                        {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : briefs.length === 0 ? (
                            <Card><CardContent>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No briefs to show. {regenLoading ? 'Generating…' : 'Hit "Regenerate now" to build today\'s digest.'}
                                </Typography>
                            </CardContent></Card>
                        ) : (
                            <Box>
                                {briefs.map((b, i) => <BriefRow key={b.id || i} brief={b} idx={i} />)}
                            </Box>
                        )}
                    </Box>
                </div>
            </div>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Regenerate today's morning brief?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will discard the cached brief and fan out fresh LLM calls (~6-8 prompts).
                        Takes 30-90s and costs roughly one cron-tick worth of LLM spend. Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleRegen} variant="contained" color="primary">Regenerate</Button>
                </DialogActions>
            </Dialog>
        </Component>
    );
}

export default MorningBriefAdmin;
